'use client';

import { BuilderBlocks } from '@builder.io/react';
import 'keen-slider/keen-slider.min.css';
import type { KeenSliderHooks, KeenSliderInstance } from 'keen-slider/react';
import type { MutableRefObject } from 'react';
import { useRef } from 'react';
import { CarouselBottomArrows } from '@src/components/Carousel/CarouselBottomArrows';
import { useBreakpoints } from '@src/utils/breakpoints.util';
import { filterBuilderAttributes } from '@src/utils/builder.util';
import { cn } from '@src/utils/tailwind';
import { CarouselArrows } from './CarouselArrows';
import { CarouselDots } from './CarouselDots';
import { type CarouselProps } from './CarouselProps';
import { useArrowPosition } from './useArrowPosition';
export type CarouselContentProps = CarouselProps & {
  isLoaded: boolean;
  moveOnKeyPress: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  sliderRef: (node: HTMLElement | null) => void;
  instanceRef: MutableRefObject<KeenSliderInstance<object, object, KeenSliderHooks> | null>;
  previousSlide: () => void;
  nextSlide: () => void;
  currentSlide: number;
};
export const CarouselContent = (props: CarouselContentProps) => {
  const {
    slides = [],
    dotsStyle,
    builderBlock,
    showArrows = true,
    showDots: showDotsOnDesktop = true,
    showDotsOnMobile = true,
    arrowColor: arrowBackgroundColor = '#BDBDBD',
    arrowIconColor = '#FFFFFF',
    overflowDesktop,
    overflowMobile,
    attributes,
    alignmentDesktop = 'center',
    alignmentMobile = 'center',
    arrowVerticalPosition = 'image',
    showBottomArrowsMobile = false,
    showBottomArrowsDesktop = false,
    isLoaded,
    moveOnKeyPress,
    sliderRef,
    instanceRef,
    previousSlide,
    nextSlide,
    currentSlide
  } = props;
  const breakpoints = useBreakpoints();
  const isMobile = breakpoints.isMobile || breakpoints.isTablet;
  const isDotsVisible = isMobile && showDotsOnMobile || !isMobile && showDotsOnDesktop;
  const slideRef = useRef<HTMLDivElement[]>([]);
  const currentAttrs = filterBuilderAttributes(attributes);
  const arrowPositions = useArrowPosition(slideRef, arrowVerticalPosition, overflowDesktop);
  // convert Builder responsive styles to tailwind classes using builder block data
  const builderStyleClass = cn({
    'lg:hidden': builderBlock?.responsiveStyles?.large?.display === 'none',
    hidden: builderBlock?.responsiveStyles?.small?.display === 'none'
  });
  return <div {...currentAttrs} className={cn(`relative flex flex-col opacity-0 lg:block ${builderStyleClass}`, {
    'lg:items-center': alignmentDesktop === 'center',
    'items-center': alignmentMobile === 'center',
    'lg:px-0': overflowDesktop,
    'builder-block': true,
    'px-0': overflowMobile,
    'opacity-100': isLoaded
  })}>
      <CarouselArrows arrowBackgroundColor={arrowBackgroundColor} arrowIconColor={arrowIconColor} arrowPositions={arrowPositions} desktopVisible={showArrows} overflowDesktop={overflowDesktop} previousSlide={previousSlide} nextSlide={nextSlide} />
      <div className={cn('w-full cursor-default lg:w-auto', {
      'w-[170%]': overflowMobile
    })} onKeyDown={moveOnKeyPress} tabIndex={0} role="button">
        <div className="keen-slider" ref={sliderRef}>
          {slides?.map((slide, i) => {
          const hasTextBlock = slide.blocks?.[0]?.children?.some((child: any) => {
            return child.component?.name === 'Text';
          });
          return slide?.blocks ? <div key={slide?.blocks[0]?.id} className="keen-slider__slide" ref={el => {
            if (el) slideRef.current[i] = el;
          }}>
                <BuilderBlocks key={i} className={cn({
              'pointer-events-none': hasTextBlock
            })} parentElementId={builderBlock?.id} blocks={slide.blocks} dataPath={`component.options.slides.${i}.blocks`} />
              </div> : <div key={i} className="keen-slider__slide" ref={el_0 => {
            if (el_0) slideRef.current[i] = el_0;
          }}>
                {slide}
              </div>;
        })}
        </div>
      </div>
      {isLoaded && instanceRef.current && isDotsVisible && <CarouselDots currentSlide={currentSlide} instanceRef={instanceRef} dotsStyle={dotsStyle} slides={slides} />}
      {isLoaded && instanceRef.current && (showBottomArrowsMobile || showBottomArrowsDesktop) && <CarouselBottomArrows mobileVisible={showBottomArrowsMobile} desktopVisible={showBottomArrowsDesktop} previousSlide={previousSlide} nextSlide={nextSlide} />}
    </div>;
};